import React, { useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import wo from './overlays.module.css'
import { useChanged, useLayer } from '../../components/layers'
import { useTasks } from '../../components/TaskCentre'
import { apiJson, inCurrentWorkspace } from '../../lib/api'
import { Failed } from '../../components/pending'
import { daysBetween, plural, rangeLabel } from './format'
import type { QueueSummary } from './types'

// "Queue a recalculation" (V4 wgRecalc): chosen workers × the period's dates
// become worker/date targets; the server skips locked days and says so.
// Queueing and rebuilding them is a background task (§10.4): the dialog
// closes as it starts, and the task drains exactly the targets it queued.

interface Props {
  user: any
  from: string
  to: string
  workers: { id: string; name: string }[]
  onClose: () => void
  // The task finished: the wage basis for its targets is rebuilt.
  onFinished: () => void
}

interface Target {
  human_id: string
  work_date: string
}

interface RecalcResponse {
  queued: number
  skipped_locked: Target[]
  queued_targets?: Target[]
  message: string
}

const CHUNK = 25
const POLL_MS = 500
const MAX_POLLS = 60
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

// Queue the targets, rebuild them a chunk at a time, and wait for any the
// background processor had already picked up. Progress is the share of this
// task's targets no longer waiting, read from the queue itself.
export async function recalculate(source: any, body: { human_ids: string[]; from: string; to: string; reason: string }, progress: (pct: number) => void) {
  // Every step below (queue, drain, poll) is for the workspace the person asked in, even if they switch
  // away while it runs: the targets are that workspace's, and no later step may be addressed to another.
  const user = inCurrentWorkspace(source)
  const r = await apiJson<RecalcResponse>(user, '/api/wages/recalculate', { body })
  const mine = r.queued_targets || []
  const locked = r.skipped_locked?.length ? ` · ${plural(r.skipped_locked.length, 'locked day', 'locked days')} left as paid` : ''
  if (!mine.length) return { note: `Nothing to rebuild${locked}` }
  const keys = new Set(mine.map((t) => `${t.human_id}|${t.work_date}`))
  const tally = (q: QueueSummary | undefined) => {
    const ours = (q?.items || []).filter((i) => keys.has(`${i.human_id}|${String(i.work_date).slice(0, 10)}`))
    return { waiting: ours.filter((i) => i.state !== 'error').length, failed: ours.filter((i) => i.state === 'error').length }
  }
  let t = { waiting: mine.length, failed: 0 }
  for (let i = 0; i < mine.length; i += CHUNK) {
    const p = await apiJson<{ queue?: QueueSummary }>(user, '/api/wages/queue/process', { body: { states: ['queued'], targets: mine.slice(i, i + CHUNK) } })
    t = tally(p.queue)
    progress(((mine.length - t.waiting) / mine.length) * 100)
  }
  // Targets the background processor held while we ran are finished by it.
  for (let n = 0; t.waiting > 0 && n < MAX_POLLS; n++) {
    await wait(POLL_MS)
    t = tally(await apiJson<QueueSummary>(user, '/api/wages/queue'))
    progress(((mine.length - t.waiting) / mine.length) * 100)
  }
  const rebuilt = mine.length - t.waiting - t.failed
  const problems = [
    t.failed ? `${plural(t.failed, 'worker/day', 'worker/days')} failed: see the recalculation queue` : '',
    t.waiting ? `${plural(t.waiting, 'worker/day is', 'worker/days are')} still in the queue; the background processor finishes them` : '',
  ].filter(Boolean)
  return { note: `${plural(rebuilt, 'worker/day', 'worker/days')} rebuilt from source${locked}`, problem: problems.join('. ') || undefined }
}

export function RecalcModal({ user, from, to, workers, onClose, onFinished }: Props) {
  const [chosen, setChosen] = useState<string[]>([])
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const { startTask } = useTasks()
  const all = chosen.length === workers.length && workers.length > 0
  const days = daysBetween(from, to).length
  // A layer (h 425-458): Escape, the scrim and Cancel ask first once a value has changed.
  const modalEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ chosen, reason })
  const { requestClose, confirm } = useLayer(true, onClose, { dirty, el: modalEl, modal: true, focusFirst: true })

  const go = () => {
    if (!chosen.length) {
      setError('Choose at least one worker')
      return
    }
    const body = { human_ids: chosen, from, to, reason: reason.trim() || 'Manual recalculation' }
    startTask({
      label: 'Recalculate wages',
      note: `${plural(chosen.length, 'worker', 'workers')} · ${rangeLabel(from, to)}`,
      run: (progress) => recalculate(user, body, progress),
      onDone: () => onFinished(),
    })
    onClose()
  }

  return (
    <>
      <div className={o.scrim} onClick={(e) => e.target === e.currentTarget && requestClose()}>
        <div ref={modalEl} tabIndex={-1} className={`${o.modal} ${wo.pm}`} role="dialog" aria-label="Queue a recalculation">
          <div className={`${o.head} ${wo.pmHead}`}>
            <div className={o.headText}>
              <h2 className={`${o.title} ${wo.pmTitle}`}>Queue a recalculation</h2>
            </div>
          </div>
          <p className={`${o.sub} ${wo.pmSub}`}>
            This queues worker/date targets. A background processor rebuilds the wage basis for those targets only, then swaps the rows in atomically. It runs under Background tasks, bottom left, while you carry on.
          </p>
          <div>
            <div className={wo.labelRow}>
              <div className={`${o.label} ${wo.pmLabel}`}>Workers</div>
              <button type="button" className={wo.miniBtn} onClick={() => setChosen(all ? [] : workers.map((w) => w.id))} disabled={!workers.length}>
                {all ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <div className={wo.chips}>
              {workers.length === 0 && <div className={wo.muted}>No worker has work in this view.</div>}
              {workers.map((w) => {
                const on = chosen.includes(w.id)
                return (
                  <button
                    key={w.id}
                    type="button"
                    aria-pressed={on}
                    className={on ? `${wo.pick} ${wo.pickOn}` : wo.pick}
                    onClick={() => setChosen((c) => (on ? c.filter((x) => x !== w.id) : c.concat(w.id)))}
                  >
                    {w.name}
                  </button>
                )
              })}
            </div>
          </div>
          <div className={wo.periodLine}>
            <span>Period</span>
            <span>{rangeLabel(from, to)}</span>
            <span className={wo.muted}>
              {(chosen.length || 'No') + ' worker' + (chosen.length === 1 ? '' : 's')} × {plural(days, 'day', 'days')} in range
            </span>
          </div>
          <div className={wo.stackSm}>
            <label className={`${o.label} ${wo.pmLabel}`} htmlFor="wg-recalc-reason">
              Reason
            </label>
            <input id="wg-recalc-reason" className={`${o.input} ${wo.insetInput}`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Recorded against the queued target…" />
          </div>
          <div className={`${o.foot} ${wo.pmFoot}`}>
            <button type="button" className={`${o.primary} ${wo.pmBtn}`} onClick={go}>
              Queue targets
            </button>
            <button type="button" className={`${o.ghost} ${wo.pmGhost}`} onClick={requestClose}>
              Cancel
            </button>
            <Failed text={error} />
          </div>
        </div>
      </div>
      {confirm}
    </>
  )
}
